/**
 * 로그인 자격증명 저장 (자동 로그인 / 아이디 저장)
 *
 * - Tauri(PC): 아이디·계열사·비밀번호를 Rust `save_login_credential`이 Windows DPAPI로 암호화해
 *   app_config_dir/login_credential.bin 에 보관. 앱 실행 시 Login 화면이 읽어 자동 로그인한다.
 * - 웹/PWA: localStorage 'savedLogin' 에 아이디·계열사만 저장(비밀번호는 절대 저장하지 않는다 —
 *   비밀번호는 브라우저 자동완성(autoComplete="current-password")에 위임).
 *
 * 정책:
 * - 사용자가 직접 로그아웃(confirmLogout) → clearSavedPassword(): 비밀번호만 지우고 아이디·autoLogin 플래그는
 *   남긴다. 다음 실행 때 아이디가 채워진 채 로그인 화면이 뜨고, 로그인하면 다시 자동 로그인이 켜진다.
 * - 401/JWT 만료 자동 로그아웃 → 아무것도 지우지 않는다(Login 마운트 시 자동 재로그인).
 * - 자동 로그인이 자격증명 오류로 거부되면 Login이 clearSavedPassword()로 재시도 루프를 끊는다.
 *
 * 모든 함수는 실패를 삼키고 warn만 남긴다(로그인 흐름을 막지 않는다).
 */
import { isTauri } from '../utils/tauri';

const WEB_KEY = 'savedLogin';

export interface SavedLogin {
  affiliateId: string;
  userid: string;
  /** Tauri에서만 채워진다. 웹은 항상 null */
  pwd: string | null;
  /** 체크박스 상태(Tauri '자동 로그인' / 웹 '아이디 저장') */
  autoLogin: boolean;
}

function parse(raw: string | null): SavedLogin | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v.userid !== 'string') return null;
    return {
      affiliateId: typeof v.affiliateId === 'string' ? v.affiliateId : '',
      userid: v.userid,
      pwd: typeof v.pwd === 'string' && v.pwd ? v.pwd : null,
      autoLogin: v.autoLogin !== false,
    };
  } catch {
    return null;
  }
}

export async function loadSavedLogin(): Promise<SavedLogin | null> {
  try {
    if (isTauri()) {
      const { invoke } = await import('@tauri-apps/api/core');
      const raw = await invoke<string | null>('load_login_credential');
      return parse(raw);
    }
    const v = parse(localStorage.getItem(WEB_KEY));
    return v ? { ...v, pwd: null } : null;
  } catch (err) {
    console.warn('[credentialService] load failed:', err);
    return null;
  }
}

export async function saveLogin(value: SavedLogin): Promise<void> {
  try {
    if (isTauri()) {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('save_login_credential', { json: JSON.stringify(value) });
      return;
    }
    // 웹은 비밀번호를 저장하지 않는다
    localStorage.setItem(WEB_KEY, JSON.stringify({ ...value, pwd: null }));
  } catch (err) {
    console.warn('[credentialService] save failed:', err);
  }
}

/** 비밀번호만 제거 (아이디·계열사·autoLogin 플래그 유지). 웹은 저장된 비밀번호가 없어 no-op */
export async function clearSavedPassword(): Promise<void> {
  if (!isTauri()) return;
  try {
    const cur = await loadSavedLogin();
    if (!cur || !cur.pwd) return;
    await saveLogin({ ...cur, pwd: null });
  } catch (err) {
    console.warn('[credentialService] clearSavedPassword failed:', err);
  }
}

export async function clearSavedLogin(): Promise<void> {
  try {
    if (isTauri()) {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('clear_login_credential');
      return;
    }
    localStorage.removeItem(WEB_KEY);
  } catch (err) {
    console.warn('[credentialService] clear failed:', err);
  }
}
